import * as yup from 'yup';
import {
	isIcelandicShipmentPhone,
	isShipmentPhoneMissingInternationalPrefix,
	isValidOptionalShipmentPhone,
	isValidSenderShipmentPhone,
	isValidShipmentPhone,
	validateForeignPostcodeGetCountryFromStore,
	validateSsn,
} from '../../../../helpers/formHelpers/formHelpers';
import { noIcelandicLettersInEmailRegex } from '../../../../helpers/regex/regex';
import { IFormSelectOption } from '../../../../interfaces/forms/forms';

export interface IStepInfoFormInfo {
	receiverSsn: string;
	receiverName: string;
	receiverAddress: string;
	receiverPostCode: IFormSelectOption;
	receiverPhone: string;
	receiverPhoneCountryCode: string;
	receiverEmail: string;
	receiverForeignPostCode: string;
	receiverForeignShipTo: string;
	receiverForeignAddressExtended: string;
	receiverForeignPlace: string;
	senderHasForeignSsn: boolean;
	senderSsn: string;
	senderName: string;
	senderAddress: string;
	senderPostCode: IFormSelectOption;
	senderPhone: string;
	senderPhoneCountryCode: string;
	senderEmail: string;
	country: IFormSelectOption;
	recipient: IFormSelectOption;
	saveRecipient: boolean;
	updateRecipient: boolean;
	chargeSenderCard: boolean;
}

export const stepInfoInitialValues: IStepInfoFormInfo = {
	receiverSsn: '',
	receiverName: '',
	receiverAddress: '',
	receiverPostCode: null,
	receiverPhone: '',
	receiverPhoneCountryCode: 'IS',
	receiverEmail: '',
	receiverForeignPostCode: '',
	receiverForeignShipTo: '',
	receiverForeignAddressExtended: '',
	receiverForeignPlace: '',
	senderHasForeignSsn: false,
	senderSsn: '',
	senderName: '',
	senderAddress: '',
	senderPostCode: null,
	senderPhone: '',
	senderPhoneCountryCode: 'IS',
	senderEmail: '',
	country: {
		label: 'Ísland',
		value: 'IS',
	},
	recipient: null,
	saveRecipient: false,
	updateRecipient: false,
	chargeSenderCard: false,
};

const yupFormSelectOption = {
	label: yup.string(),
	value: yup.string(),
};

const shipmentPhoneValidationMessage = (value?: string) =>
	isIcelandicShipmentPhone(value)
		? 'step-info.mobile-format'
		: isShipmentPhoneMissingInternationalPrefix(value)
		? 'step-info.phone-international-prefix'
		: 'step-info.phone-format';

const senderPhoneValidationMessage = (value?: string) =>
	isShipmentPhoneMissingInternationalPrefix(value)
		? 'step-info.phone-international-prefix'
		: 'step-info.phone-format';

const validateOptionalShipmentPhone = function(value?: string) {
	return (
		isValidOptionalShipmentPhone(value) ||
		this.createError({ message: shipmentPhoneValidationMessage(value) })
	);
};

const validateRequiredSenderShipmentPhone = function(value?: string) {
	return (
		isValidSenderShipmentPhone(value || '') ||
		this.createError({ message: senderPhoneValidationMessage(value) })
	);
};

export const StepInfoSchema = yup.object().shape({
	receiverSsn: yup.string().test('is-valid-ssn', 'step-info.ssn-valid', validateSsn),
	receiverName: yup.string().max(44, 'step-info.max').required('step-info.name-required'),
	receiverAddress: yup.string().max(35, 'step-info.max').required('step-info.address-required'),
	receiverPostCode: yup
		.object()
		.nullable()
		.when('country', {
			is: val => (val ? val.value === 'IS' : false),
			then: yup.object().nullable().shape(yupFormSelectOption).required('step-info.poscode-required'),
		}),
	receiverPhone: yup
		.string()
		.when('country', {
			is: val => (val ? val.value === 'IS' : false),
			then: yup
				.string()
				.test('is-valid-shipment-phone', 'step-info.phone-format', validateOptionalShipmentPhone),
		})
		.when('country', {
			is: val => (val ? val.value !== 'IS' : false),
			then: yup
				.string()
				.test('is-valid-shipment-phone', 'step-info.phone-format', validateOptionalShipmentPhone),
		}),
	receiverEmail: yup
		.string()
		.max(80, 'step-info.max')
		.matches(noIcelandicLettersInEmailRegex, 'step-info.no-icelandic-letters')
		.email('step-info.email-format')
		.when('country', {
			is: val => (val ? val.value !== 'IS' : false),
			then: yup
				.string()
				.nullable()
				.when('receiverPhone', {
					is: phone => !(phone && phone.trim().length > 0),
					then: yup.string().required('step-info.required-email-or-phone').email('step-info.email-format'),
				}),
		}),
	receiverForeignPlace: yup
		.string()
		.nullable()
		.when('country', {
			is: val => (val ? val.value !== 'IS' : false),
			then: yup.string().max(35, 'step-info.max').required('step-info.field-required'),
		}),
	receiverForeignAddressExtended: yup
		.string()
		.nullable()
		.when('country', {
			is: val => (val ? val.value !== 'IS' : false),
			then: yup.string().max(35, 'step-info.max'),
		}),
	receiverForeignShipTo: yup
		.string()
		.nullable()
		.when('country', {
			is: val => (val ? val.value !== 'IS' : false),
			then: yup.string().max(35, 'step-info.max'),
		}),
	receiverForeignPostCode: yup
		.string()
		.nullable()
		.when('country', {
			is: val => (val ? val.value !== 'IS' : false),
			then: yup
				.string()
				.max(15, 'step-info.max')
				.required('step-info.field-required')
				.test('is-valid-ssn', 'step-info.zip-incorrect', function (value) {
					return validateForeignPostcodeGetCountryFromStore(
						value,
						this.createError,
						'receiverForeignPostCode'
					);
				}),
		}),
	senderHasForeignSsn: yup.boolean().when('payer', {
		is: 'vidtakandi',
		then: yup.bool().oneOf([false], 'step-info.social-id-required'),
	}),
	senderSsn: yup
		.string()
		.test('is-valid-ssn', 'step-info.ssn-valid', validateSsn)
		.when('senderHasForeignSsn', {
			is: false,
			then: yup
				.string()
				.required('step-info.ssn-required')
				.min(10, 'step-info.ssn-min')
				.test('is-valid-ssn', 'step-info.ssn-valid', validateSsn),
		}),
	senderName: yup.string().max(44, 'step-info.max').required('step-info.name-required'),
	senderAddress: yup.string().max(35, 'step-info.max').required('step-info.address-required'),
	senderPostCode: yup.object().nullable().shape(yupFormSelectOption).required('step-info.poscode-required'),
	senderPhone: yup
		.string()
		.test('is-valid-sender-shipment-phone', 'step-info.phone-format', validateRequiredSenderShipmentPhone)
		.required('step-info.phone-required'),
	senderEmail: yup
		.string()
		.email('step-info.email-format')
		.matches(noIcelandicLettersInEmailRegex, 'step-info.no-icelandic-letters')
		.required('step-info.email-required'),
	saveRecipient: yup.boolean(),
	updateRecipient: yup.boolean(),
	chargeSenderCard: yup.boolean(),
});
